// src/app/components/auth/components/PassWordInput.tsx
import React, { useState, forwardRef } from 'react';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import Input from 'components/ui/Input/Input';

// Define the props for your component.
export interface PassWordInputProps {
  register: any;
  className?: string;
  name?: string;
  placeholder?: string;
  autoComplete?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

// Use React.ForwardRefRenderFunction to type both the props and the forwarded ref.
const PassWordInput: React.ForwardRefRenderFunction<HTMLInputElement, PassWordInputProps> = (
  {
    register,
    className = null,
    name = 'password',
    placeholder = 'Password',
    autoComplete = 'password',
    onChange = () => {},
  },
  ref
) => {
  const [passwordMasked, setPasswordMasked] = useState(true);
  const { errors, watch } = useFormContext();

  // Create a combined ref that registers the element with RHF and forwards the ref.
  const combinedRef = (element: HTMLInputElement | null) => {
    // Register the element with react-hook-form.
    register({
      required: 'Please enter password',
      minLength: { value: 6, message: 'Your password must be at least 6 characters' },
    })(element);

    // Forward the ref.
    if (ref) {
      if (typeof ref === 'function') {
        ref(element);
      } else {
        const forwardedRef = ref as React.MutableRefObject<HTMLInputElement | null>;
        forwardedRef.current = element;
      }
    }
  };

  return (
    <Wrapper className={className}>
      <Input
        placeholder={placeholder}
        type={passwordMasked ? 'password' : 'text'}
        name={name}
        data-testid="password-input"
        hasValue={!!watch(name)}
        hasError={!!errors?.[name]}
        autoComplete={autoComplete}
        onChange={onChange}
        inputRef={combinedRef}
      />
      <Action
        tabIndex={-1}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setPasswordMasked(!passwordMasked);
        }}
      >
        <img
          src={passwordMasked ? '/images/eyecon.svg' : '/images/eyecon-open.svg'}
          style={{ maxWidth: '100%' }}
          alt={passwordMasked ? 'Hide password' : 'Reveal password'}
        />
      </Action>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const Action = styled.button`
  position: absolute;
  right: 15px;
  top: 13px;
`;

// Export the component with ref forwarding.
export default forwardRef(PassWordInput);
